/* The AI panel — the voice and photo assistant.
   ==============================================

   Everything it sends goes through otterAI(), which posts to the Netlify
   function. The function never reads app data from Firestore, so the model
   only ever sees what this browser already had — and this browser only holds
   what the signed-in user is allowed to have, because every cache is filled by
   a scoped listener. That guarantee falls out of the scoping; it is not
   something this file has to remember to enforce.

   Moved out of app.html unchanged apart from the onReady() wrapper, which is
   required: it opened with `if (!overlay) return;` and would silently disable
   itself if that ran before the markup existed.
*/

import { onReady } from '../lib/ready.js';

export function initAiPanel() {
  const $ = id => document.getElementById(id);
  const overlay = $('ai-overlay');
  if (!overlay) return;   // now only true if the markup is genuinely absent

  let aiResult = null;   // last thing the model returned
  let aiDest = 'note';

  const AI_STAGES = ['pick', 'say', 'answer', 'act', 'busy', 'confirm'];
  function stage(name) {
    AI_STAGES.forEach(s => {
      const el = $('ai-stage-' + s);
      if (el) el.hidden = (s !== name);
    });
  }

  function aiOpen() {
    aiResult = null;
    aiDest = 'note';
    aiMode = 'command';
    aiIv = null;
    stage('pick');
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
  }
  function aiClose() {
    aiStopMic();
    aiStopSpeaking();
    aiClearAutoSend();
    aiMode = 'command';
    aiIv = null;
    overlay.hidden = true;
    document.body.style.overflow = '';
    aiTurns = [];
    aiPending = null;
    // Drop the read result on the way out. Nothing about a page the CHW chose
    // not to file should survive in memory for the next one to inherit.
    aiResult = null;
    const f = $('ai-file'); if (f) f.value = '';
  }

  /* Downscale and re-encode. 1568px is the longest edge the model uses, so
     anything larger is paid for and thrown away. The canvas round-trip is also
     what strips EXIF — including the GPS coordinates a phone writes into every
     photo, which at a client's home is their address. */
  const AI_MAX_EDGE = 1568;
  async function aiPrepareImage(file) {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
      // Older Safari, or a format the browser cannot decode (HEIC direct from
      // a camera roll is the usual culprit).
      throw new Error('That image could not be opened. Try taking the photo again, or use a JPEG.');
    }
    const scale = Math.min(1, AI_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    return { media_type: 'image/jpeg', data: dataUrl.split(',')[1] };
  }

  /* Match a name the model read off the page against clients ALREADY IN THIS
     BROWSER. The roster never goes to the API — the model reports what it saw
     and the matching happens here. Exact beats prefix beats shared-token, and
     an ambiguous match deliberately returns nothing so the CHW picks. */
  function aiMatchClient(query) {
    const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
    const q = String(query || '').trim().toLowerCase();
    if (!q || !pool.length) return null;
    const norm = s => String(s || '').trim().toLowerCase();

    const exact = pool.filter(c => norm(c.name) === q);
    if (exact.length === 1) return exact[0];

    const starts = pool.filter(c => norm(c.name).startsWith(q) || q.startsWith(norm(c.name)));
    if (starts.length === 1) return starts[0];

    const qTokens = q.split(/\s+/).filter(t => t.length > 1);
    const scored = pool.map(c => {
      const tokens = norm(c.name).split(/\s+/);
      const hits = qTokens.filter(t => tokens.some(w => w === t || w.startsWith(t))).length;
      return { c, hits };
    }).filter(x => x.hits > 0).sort((a, b) => b.hits - a.hits);
    // Only accept a token match if one candidate clearly beats the rest.
    if (scored.length === 1) return scored[0].c;
    if (scored.length > 1 && scored[0].hits > scored[1].hits) return scored[0].c;
    return null;
  }

  function aiFillClientSelect(matched) {
    const sel = $('ai-client');
    const hint = $('ai-client-hint');
    if (!sel) return;
    const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
    sel.innerHTML = '<option value="">— nobody in particular —</option>' +
      pool.map(c => `<option value="${c.id}">${(c.name || 'Unnamed').replace(/</g, '&lt;')}</option>`).join('');
    if (matched) sel.value = matched.id;
    if (hint) {
      const q = aiResult && aiResult.personQuery;
      if (q && matched) { hint.textContent = `Read "${q}" on the page — matched to ${matched.name}. Change it if that's wrong.`; hint.hidden = false; }
      else if (q) { hint.textContent = `Read "${q}" on the page but found no one by that name. Pick them yourself, or leave it blank.`; hint.hidden = false; }
      else { hint.hidden = true; }
    }
  }

  function aiShowConfirm(r) {
    aiResult = r || {};
    const followUps = Array.isArray(r.followUps) ? r.followUps.filter(Boolean) : [];
    let body = String(r.body || '').trim();
    if (followUps.length) {
      body += '\n\nFollow-ups\n' + followUps.map(f => '• ' + f).join('\n');
    }
    $('ai-title').value = String(r.title || '').slice(0, 120);
    $('ai-body').value = body;

    $('ai-health-warn').hidden = !r.containsHealthInfo;

    const rw = $('ai-read-warn');
    const problems = [];
    if (r.unreadable) problems.push('Much of this page could not be read. Check it carefully against the paper before saving.');
    if (r.notes) problems.push(String(r.notes));
    if (problems.length) { rw.textContent = problems.join(' '); rw.hidden = false; }
    else { rw.hidden = true; }

    aiFillClientSelect(aiMatchClient(r.personQuery));
    aiSetDest('note');
    stage('confirm');
  }

  function aiSetDest(d) {
    aiDest = d;
    document.querySelectorAll('.ai-dest-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.dest === d);
    });
  }

  async function aiHandleFile(file) {
    if (!file) return;
    stage('busy');
    $('ai-busy-msg').textContent = 'Preparing the photo…';
    try {
      const image = await aiPrepareImage(file);
      $('ai-busy-msg').textContent = 'Reading the page…';
      const r = await window.otterAI('readPaper', { image });
      aiShowConfirm(r);
    } catch (e) {
      console.warn('AI read failed:', e && e.message);
      alert(e && e.message ? e.message : 'Could not read that photo.');
      stage('pick');
    } finally {
      const f = $('ai-file'); if (f) f.value = '';
    }
  }

  /* Meeting Mode's mmToast lives inside its own closure and isn't reachable
     from here, so this panel carries its own. Deliberately not a global — one
     more shared name is one more thing to collide with in a 19,000-line file. */
  function aiToast(text) {
    const t = document.createElement('div');
    t.textContent = text;
    t.style.cssText = 'position:fixed; left:50%; bottom:28px; transform:translateX(-50%);' +
      'background:#1f2430; color:#fff; padding:11px 18px; border-radius:999px; font-size:13.5px;' +
      'box-shadow:0 8px 26px rgba(0,0,0,.3); z-index:9500; opacity:0; transition:opacity .18s ease;';
    document.body.appendChild(t);
    requestAnimationFrame(() => { t.style.opacity = '1'; });
    setTimeout(() => {
      t.style.opacity = '0';
      setTimeout(() => t.remove(), 250);
    }, 2600);
  }

  async function aiSave() {
    const btn = $('ai-save');
    const title = ($('ai-title').value || '').trim();
    const body = ($('ai-body').value || '').trim();
    const clientId = $('ai-client').value || '';
    if (!body) { alert('There is nothing to save.'); return; }

    const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
    const client = clientId ? pool.find(c => c.id === clientId) : null;

    if (aiDest === 'contact' && !client) {
      alert('A contact log entry has to belong to someone. Pick a person, or file it as a note instead.');
      return;
    }

    btn.disabled = true;
    try {
      if (aiDest === 'contact') {
        const date = (aiResult && aiResult.date) || new Date().toISOString().slice(0, 10);
        const text = title ? (title + '\n' + body) : body;
        const ok = addClientContact(client.id, date, text);
        if (!ok) throw new Error('Could not add that to the contact log.');
      } else {
        // Reuses notesCreate so the rules-relevant fields (clientAssignedUids,
        // sharedWith) are computed by the same code as every other note. Title
        // is patched immediately afterwards because notesCreate takes a body
        // but no title.
        await notesCreate(client ? { id: client.id, name: client.name } : null, body, 'note');
        // Stamp the origin so the Notes "Scanned" filter can find it later.
        notesPatchActive(title ? { title, source: 'scan' } : { source: 'scan' }, true);
      }
      aiClose();
      aiToast(aiDest === 'contact'
        ? 'Added to ' + client.name + '’s contact log ✓'
        : 'Filed as a note ✓');
    } catch (e) {
      console.warn('AI save failed:', e && e.message);
      alert(e && e.message ? e.message : 'Could not save that.');
    } finally {
      btn.disabled = false;
    }
  }

  /* ======================================================================
     THE COMMAND LAYER
     One sentence in. Either a question the browser answers by itself, or a
     write the user confirms. See netlify/functions/ai.js for why the model
     never receives records.
     ====================================================================== */

  let aiTurns = [];        // the conversation so far, for clarification rounds
  let aiPending = null;    // the action awaiting confirmation

  const aiToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const aiMyUid = () => (typeof currentUser !== 'undefined' && currentUser) ? currentUser.uid : null;

  function aiRenderTurns() {
    const box = $('ai-turns');
    box.innerHTML = aiTurns.map(t =>
      `<div class="ai-turn ai-turn-${t.role === 'user' ? 'you' : 'ai'}">` +
      `<b>${t.role === 'user' ? 'You' : 'Assistant'}</b>` +
      String(t.text).replace(/</g, '&lt;') + '</div>'
    ).join('');
  }

  function aiOpenSay(reset) {
    if (reset !== false) { aiTurns = []; aiPending = null; aiHideChoices(); }
    aiRenderTurns();
    $('ai-utterance').value = '';
    stage('say');
    setTimeout(() => $('ai-utterance').focus(), 60);
  }

  async function aiSendUtterance() {
    const box = $('ai-utterance');
    const said = (box.value || '').trim();
    if (!said) { box.focus(); return; }
    aiClearAutoSend();
    /* Mid-interview the microphone stays on — the whole point is not touching
       the device between questions. Outside it, sending ends the dictation. */
    if (aiMode === 'clientIv') { aiSendInterviewAnswer(said); return; }
    aiStopMic();
    aiTurns.push({ role: 'user', text: said });
    stage('busy');
    $('ai-busy-msg').textContent = 'Working out what you meant…';
    try {
      const r = await window.otterAI('command', { today: aiToday(), turns: aiTurns });
      aiHandleCommand(r || {});
    } catch (e) {
      console.warn('AI command failed:', e && e.message);
      alert(e && e.message ? e.message : 'Could not understand that.');
      aiOpenSay(false);
    }
  }

  function aiHandleCommand(r) {
    if (r.kind === 'clarify' && r.clarify) {
      aiTurns.push({ role: 'assistant', text: r.clarify });
      aiOpenSay(false);
      aiSpeak(r.clarify);
      return;
    }
    if (r.kind === 'chat' && r.reply) {
      // Conversation stays in the same view, so it reads as a back-and-forth
      // rather than bouncing the CHW between screens.
      aiTurns.push({ role: 'assistant', text: r.reply });
      aiOpenSay(false);
      aiSpeak(r.reply);
      return;
    }
    if (r.kind === 'question' && r.query) {
      const answer = aiRunQuery(r.query);
      $('ai-answer-said').textContent = r.say || '';
      $('ai-answer-body').innerHTML = answer;
      stage('answer');
      // Speak the plain-language framing, not the markup.
      aiSpeak(r.say);
      return;
    }
    if (r.kind === 'action' && r.action === 'addClient') {
      /* Enrolling somebody is the one action with six fields, and it is also
         the one most likely to happen while standing in front of the person.
         So instead of a form, it starts a spoken interview seeded with
         whatever they already said. */
      aiStartClientInterview(r.client || (r.personQuery ? { name: r.personQuery } : {}));
      return;
    }
    if (r.kind === 'action' && r.action) {
      aiShowAction(r);
      return;
    }
    // "unsupported", or something the model returned in a shape we don't use.
    const msg = r.say || "That isn't something I can do in here.";
    aiTurns.push({ role: 'assistant', text: msg });
    aiOpenSay(false);
    aiSpeak(msg);
  }

  /* ---------------------------------------------------------------------
     LOCAL QUERY ENGINE
     Runs entirely against caches this browser already holds. Those caches are
     filled by scoped Firestore listeners, so they contain only records this
     user is allowed to see — a member's browser simply does not hold anyone
     else's mileage. That is why this can be trusted without a permission
     check of its own.

     Admins DO hold everyone's records, so anything phrased as "my" is
     filtered by uid here as well. Otherwise an owner asking "how many miles
     did I drive" would get the whole team's total.
     --------------------------------------------------------------------- */

  const aiInRange = (d, from, to) => {
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };
  const aiEsc = s => String(s == null ? '' : s).replace(/</g, '&lt;');
  const aiBig = (n, label) => `<span class="ai-big-num">${n}</span>${aiEsc(label)}`;
  const aiNone = msg => `<span class="ai-none">${aiEsc(msg)}</span>`;
  const aiList = items => items.length
    ? '<ul>' + items.map(i => `<li>${aiEsc(i)}</li>`).join('') + '</ul>'
    : aiNone('Nothing matches.');

  function aiRunQuery(q) {
    const uid = aiMyUid();
    const from = q.from || null, to = q.to || null;
    const mine = row => !uid || row.addedBy === uid;

    try {
      if (q.what === 'mileage') {
        const rows = (typeof teamMileageCache !== 'undefined' ? teamMileageCache : [])
          .filter(e => mine(e) && aiInRange(e.date, from, to));
        if (q.metric === 'list') {
          return aiList(rows.sort((a, b) => a.date.localeCompare(b.date))
            .map(e => `${e.date} — ${(+e.totalMiles || 0).toFixed(1)} miles`));
        }
        const miles = rows.reduce((s, e) => s + (+e.totalMiles || 0), 0);
        const pay = rows.reduce((s, e) => s + (+e.totalPay || 0), 0);
        return aiBig(miles.toFixed(1), ' miles') +
          `<div>across ${rows.length} day${rows.length === 1 ? '' : 's'}` +
          (pay ? ` · $${pay.toFixed(2)} reimbursable` : '') + '</div>';
      }

      if (q.what === 'hours') {
        const rows = (typeof teamMemberHoursCache !== 'undefined' ? teamMemberHoursCache : [])
          .filter(e => mine(e) && aiInRange(e.date, from, to));
        if (q.metric === 'list') {
          return aiList(rows.sort((a, b) => a.date.localeCompare(b.date))
            .map(e => `${e.date} — ${e.name || 'Unnamed'}, ${Math.round((+e.totalMinutes || 0))} min`));
        }
        const mins = rows.reduce((s, e) => s + (+e.totalMinutes || 0), 0);
        const h = Math.floor(mins / 60), m = Math.round(mins % 60);
        return aiBig(h + 'h ' + m + 'm', '') +
          `<div>across ${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}</div>`;
      }

      if (q.what === 'contacts') {
        const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
        const named = q.personQuery ? aiMatchClient(q.personQuery) : null;
        let n = 0; const lines = [];
        pool.forEach(c => {
          if (named && c.id !== named.id) return;
          let contacts = [];
          try { contacts = JSON.parse(c.contactsJson || '[]'); } catch (e) { contacts = c.contacts || []; }
          contacts.forEach(ct => {
            if (!aiInRange(ct.date, from, to)) return;
            if (uid && ct.by && ct.by !== uid) return;
            n++;
            lines.push(`${ct.date} — ${c.name}${ct.note ? ': ' + String(ct.note).slice(0, 70) : ''}`);
          });
        });
        if (q.metric === 'list') return aiList(lines.sort());
        return aiBig(n, n === 1 ? ' contact' : ' contacts') +
          (named ? `<div>with ${aiEsc(named.name)}</div>` : '');
      }

      if (q.what === 'clients') {
        const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
        if (q.staleDays) {
          const cutoff = new Date(Date.now() - q.staleDays * 864e5).toISOString().slice(0, 10);
          const stale = pool.filter(c => {
            const last = c.lastContactedAt || '';
            return !last || last < cutoff;
          });
          return `<div>${stale.length} of ${pool.length} not contacted in ${q.staleDays} days</div>` +
            aiList(stale.map(c => c.name + (c.lastContactedAt ? ` — last ${c.lastContactedAt}` : ' — never')));
        }
        if (q.metric === 'list') return aiList(pool.map(c => c.name));
        return aiBig(pool.length, pool.length === 1 ? ' person' : ' people') + '<div>assigned to you</div>';
      }

      if (q.what === 'referrals') {
        const rows = (typeof teamReferralsCache !== 'undefined' ? teamReferralsCache : [])
          .filter(r => !from || aiInRange(r.dateReceived, from, to));
        if (q.metric === 'list') return aiList(rows.map(r => (r.name || 'Unnamed') +
          (r.dateReceived ? ' — ' + r.dateReceived : '')));
        return aiBig(rows.length, rows.length === 1 ? ' referral' : ' referrals');
      }

      if (q.what === 'followups') {
        const rows = (typeof teamEventsCache !== 'undefined' ? teamEventsCache : [])
          .filter(e => e && e.type !== '__topic__' && !e.done)
          .filter(e => mine(e))
          .filter(e => (!from && !to) ? true : aiInRange(e.date, from, to));
        if (q.metric === 'count') return aiBig(rows.length, rows.length === 1 ? ' open item' : ' open items');
        return aiList(rows.sort((a, b) => String(a.date).localeCompare(String(b.date)))
          .map(e => `${e.date || 'no date'} — ${e.title || 'Untitled'}`));
      }
    } catch (e) {
      console.warn('AI local query failed:', e);
      return aiNone('Something went wrong working that out. Check the console.');
    }
    return aiNone("I don't know how to look that up yet.");
  }

  /* ---------------------------------------------------------------------
     ACTION CONFIRMATION
     Every field the model proposed is shown as an editable input. The CHW is
     correcting a draft, not approving a black box — which is also what makes
     a wrong guess cheap instead of dangerous.
     --------------------------------------------------------------------- */

  const AI_ACTION_LABELS = {
    logMileage:  'Log mileage',
    logHours:    'Log member hours',
    logContact:  'Record a contact',
    addFollowUp: 'Add a follow-up',
    createNote:  'Create a note',
    addClient:   'Add a new client'
  };

  function aiField(id, label, value, type) {
    return `<div class="ai-field"><label class="ai-label" for="${id}">${aiEsc(label)}</label>` +
      (type === 'textarea'
        ? `<textarea id="${id}" class="ai-input ai-textarea" rows="3">${aiEsc(value)}</textarea>`
        : `<input id="${id}" class="ai-input" type="${type || 'text'}" value="${aiEsc(value)}" />`) +
      '</div>';
  }

  function aiClientSelectHtml(id, matched) {
    const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
    return `<div class="ai-field"><label class="ai-label" for="${id}">Person</label>` +
      `<select id="${id}" class="ai-input"><option value="">— nobody in particular —</option>` +
      pool.map(c => `<option value="${c.id}"${matched && matched.id === c.id ? ' selected' : ''}>` +
        aiEsc(c.name || 'Unnamed') + '</option>').join('') + '</select></div>';
  }

  function aiShowAction(r) {
    aiPending = r;
    const date = r.date || aiToday();
    const matched = r.personQuery ? aiMatchClient(r.personQuery) : null;
    let html = `<p class="ai-label" style="margin-top:0">${aiEsc(AI_ACTION_LABELS[r.action] || r.action)}</p>`;

    if (r.action === 'logMileage') {
      html += aiField('ai-f-miles', 'Miles', r.miles != null ? r.miles : '', 'number');
      html += aiField('ai-f-date', 'Date', date, 'date');
      html += aiField('ai-f-note', 'Note (optional)', r.note || '', 'textarea');
    } else if (r.action === 'logHours') {
      html += aiClientSelectHtml('ai-f-client', matched);
      html += aiField('ai-f-minutes', 'Minutes', r.minutes != null ? r.minutes : '', 'number');
      html += aiField('ai-f-date', 'Date', date, 'date');
    } else if (r.action === 'logContact') {
      html += aiClientSelectHtml('ai-f-client', matched);
      html += aiField('ai-f-date', 'Date', date, 'date');
      html += aiField('ai-f-note', 'What happened', r.note || r.say || '', 'textarea');
    } else if (r.action === 'addFollowUp') {
      html += aiField('ai-f-title', 'Follow-up', r.title || r.note || '', 'text');
      html += aiField('ai-f-date', 'Date', date, 'date');
      html += aiClientSelectHtml('ai-f-client', matched);
    } else if (r.action === 'addClient') {
      /* Only the name is asked for out loud. The rest are shown as empty
         fields to fill in if they happen to be to hand — a CHW standing in
         front of someone should not have to dictate a birthday and a CIN
         aloud just to create the record. */
      const c = r.client || {};
      const enroll = c.enrollmentDate || (typeof todayISO === 'function' ? todayISO() : '');
      const expire = c.expirationDate ||
        ((typeof addMonthsISO === 'function' && enroll)
          ? addMonthsISO(enroll, (typeof RENEWAL_MONTHS !== 'undefined' ? RENEWAL_MONTHS : 6)) : '');
      const sources = aiRefSources();
      html += aiField('ai-f-cname',     'Name',                c.name || r.personQuery || '', 'text');
      html += aiField('ai-f-cphone',    'Phone',               c.phone || '', 'tel');
      html += aiField('ai-f-caddress',  'Address',             c.address || '', 'text');
      html += aiField('ai-f-cbirthday', 'Birthday',            c.birthday || '', 'date');
      html += aiField('ai-f-ccin',      'CIN (last 4)',        c.cin || '', 'text');
      html += `<div class="ai-field"><label class="ai-label" for="ai-f-cref">Referred by</label>` +
        `<select id="ai-f-cref" class="ai-input"><option value="">— none —</option>` +
        sources.map(sname => `<option value="${aiEsc(sname)}"` +
          (c.referredBy && c.referredBy.toLowerCase() === String(sname).toLowerCase() ? ' selected' : '') +
          `>${aiEsc(sname)}</option>`).join('') + '</select></div>';
      html += aiField('ai-f-cenroll',   'Enrollment date',     enroll, 'date');
      html += aiField('ai-f-cexpire',   'Expiration date',     expire, 'date');
      html += `<p class="ai-fine">You'll be assigned to them automatically. Enrollment defaults to today and expiration to six months later, same as the form. Anything blank can be filled in later on their profile.</p>`;
    } else if (r.action === 'createNote') {
      html += aiField('ai-f-title', 'Title', r.title || '', 'text');
      html += aiField('ai-f-note', 'Note', r.note || '', 'textarea');
      html += aiClientSelectHtml('ai-f-client', matched);
    } else {
      html += `<p class="ai-lede">${aiEsc(r.say || '')}</p>`;
    }

    if (r.personQuery && !matched) {
      html += `<p class="ai-fine">Heard "${aiEsc(r.personQuery)}" but found nobody by that name in your list. Pick them above.</p>`;
    }

    $('ai-act-said').textContent = r.say || '';
    $('ai-act-fields').innerHTML = html;
    stage('act');
  }

  const aiVal = id => { const e = $(id); return e ? (e.value || '').trim() : ''; };

  async function aiSaveAction() {
    if (!aiPending) return;
    const btn = $('ai-act-save');
    const a = aiPending.action;
    const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
    const clientId = aiVal('ai-f-client');
    const client = clientId ? pool.find(c => c.id === clientId) : null;
    const date = aiVal('ai-f-date') || aiToday();

    btn.disabled = true;
    try {
      if (a === 'logMileage') {
        const miles = parseFloat(aiVal('ai-f-miles'));
        if (!(miles > 0)) throw new Error('Enter how many miles.');
        await aiWriteMileage(date, miles, aiVal('ai-f-note'));
        aiToast(miles + ' miles logged for ' + date + ' ✓');

      } else if (a === 'logHours') {
        const minutes = parseInt(aiVal('ai-f-minutes'), 10);
        if (!(minutes > 0)) throw new Error('Enter how many minutes.');
        if (!client) throw new Error('Pick who these hours were with.');
        if (typeof mhTeamAdd !== 'function') throw new Error('Member hours are not available here.');
        await mhTeamAdd({ name: client.name, date, totalMinutes: minutes,
          units: Math.round(minutes / 15), createdAt: new Date().toISOString() });
        aiToast(minutes + ' minutes logged for ' + client.name + ' ✓');

      } else if (a === 'logContact') {
        if (!client) throw new Error('Pick who you contacted.');
        const ok = addClientContact(client.id, date, aiVal('ai-f-note'));
        if (!ok) throw new Error('Could not add that to the contact log.');
        aiToast('Contact recorded for ' + client.name + ' ✓');

      } else if (a === 'addFollowUp') {
        const title = aiVal('ai-f-title');
        if (!title) throw new Error('Give the follow-up a name.');
        await aiWriteFollowUp(title, date, client);
        aiToast('Follow-up added for ' + date + ' ✓');

      } else if (a === 'createNote') {
        const body = aiVal('ai-f-note');
        if (!body) throw new Error('There is nothing to save.');
        await notesCreate(client ? { id: client.id, name: client.name } : null, body, 'note');
        const t = aiVal('ai-f-title');
        if (t) notesPatchActive({ title: t }, true);
        aiToast('Note saved ✓');

      } else if (a === 'addClient') {
        const name = aiVal('ai-f-cname');
        if (!name) throw new Error('A client needs a name.');
        await aiWriteClient({
          name,
          phone:      aiVal('ai-f-cphone'),
          address:    aiVal('ai-f-caddress'),
          birthday:   aiVal('ai-f-cbirthday'),
          cin:        aiVal('ai-f-ccin'),
          referredBy: aiVal('ai-f-cref'),
          enroll:     aiVal('ai-f-cenroll'),
          expire:     aiVal('ai-f-cexpire')
        });
        aiToast(name + ' added ✓');

      } else {
        throw new Error('That action is not supported.');
      }
      aiClose();
    } catch (e) {
      console.warn('AI action save failed:', e && e.message);
      alert(e && e.message ? e.message : 'Could not save that.');
    } finally {
      btn.disabled = false;
    }
  }

  /* Mileage has one document per person per day, so a spoken entry ADDS to
     whatever is already recorded for that date rather than creating a second
     row that the mileage screen would show as a duplicate. The document shape
     mirrors mirrorDayToTeamMileage — keep the two in step if that changes. */
  async function aiWriteMileage(date, miles, note) {
    if (!(typeof fbDb !== 'undefined' && fbDb && activeTeamId && currentUser)) {
      throw new Error('Open a team before logging mileage.');
    }
    const col = fbDb.collection('teams').doc(activeTeamId).collection('mileage');
    const existing = (typeof teamMileageCache !== 'undefined' ? teamMileageCache : [])
      .find(e => e.addedBy === currentUser.uid && e.date === date);
    const rate = (typeof state !== 'undefined' && state && state.rate) ? state.rate : 0;
    const myName = (typeof myTeamDisplayName === 'function' ? myTeamDisplayName() : '')
      || currentUser.displayName || currentUser.email || 'Member';

    if (existing) {
      const total = (+existing.totalMiles || 0) + miles;
      await col.doc(existing.id).update({
        totalMiles: total,
        totalPay: +(total * (existing.rate || rate)).toFixed(2),
        updatedAt: new Date().toISOString()
      });
    } else {
      await col.add({
        date, totalMiles: miles, totalPay: +(miles * rate).toFixed(2), rate,
        stopsJson: '[]', legsJson: '[]',
        addedBy: currentUser.uid, addedByName: myName,
        source: 'voice', note: note || '',
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
      });
      if (typeof logTeamActivity === 'function') logTeamActivity('mileage_added', { targetName: date });
    }
  }

  /* Same document shape addTeamClientFromForm writes — keep the two in step.

     assignedUids MUST contain the creator: the Firestore rule for clients
     requires it on create, and without it the record would be orphaned and
     invisible to the person who just made it. */
  async function aiWriteClient(c) {
    if (!(typeof fbDb !== 'undefined' && fbDb && activeTeamId && currentUser)) {
      throw new Error('Open a team before adding a client.');
    }
    const myName = (typeof myTeamDisplayName === 'function' ? myTeamDisplayName() : '')
      || currentUser.displayName || currentUser.email || 'Member';
    const enroll = c.enroll || (typeof todayISO === 'function' ? todayISO() : '');
    const expire = c.expire ||
      ((typeof addMonthsISO === 'function' && enroll)
        ? addMonthsISO(enroll, (typeof RENEWAL_MONTHS !== 'undefined' ? RENEWAL_MONTHS : 6)) : '');
    const payload = {
      name: c.name, phone: c.phone || '',
      // Last four, alphanumeric, uppercase — exactly what the manual form stores.
      cin: String(c.cin || '').replace(/[^A-Za-z0-9]/g, '').slice(-4).toUpperCase(),
      birthday: c.birthday || '', address: c.address || '',
      addressLat: null, addressLon: null,
      enrollmentDate: enroll, expirationDate: expire,
      referredBy: c.referredBy || '',
      contactsJson: '[]',
      assignedUids: [currentUser.uid],
      addedBy: currentUser.uid, addedByName: myName,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      lastContactedAt: null, lastContactedBy: null, lastContactedByName: null
    };
    const ref = await fbDb.collection('teams').doc(activeTeamId).collection('clients').add(payload);
    if (typeof logTeamActivity === 'function') {
      logTeamActivity('added_clients', { meta: { count: 1 }, targetName: c.name });
    }
    // Same courtesy as the manual form: look up the address in the background
    // so the client appears on the mileage map without anyone doing anything.
    if (c.address && typeof geocodeTeamClients === 'function') {
      geocodeTeamClients([{ id: ref.id, address: c.address }]);
    }
    return ref.id;
  }

  /* Same document shape the calendar's add-modal writes. */
  async function aiWriteFollowUp(title, date, client) {
    const notes = client ? ('For ' + client.name) : '';
    if (typeof calTeamMode === 'function' && calTeamMode()) {
      const doc = {
        type: 'followup', title, date, time: '', notes, topic: '', color: '',
        done: false,
        addedBy: currentUser.uid,
        addedByName: (currentUser.displayName || currentUser.email || 'member'),
        createdAt: new Date().toISOString()
      };
      const ref = await fbDb.collection('teams').doc(activeTeamId).collection('events').add(doc);
      if (typeof teamEventsCache !== 'undefined' && !teamEventsCache.find(e => e.id === ref.id)) {
        teamEventsCache.push({ id: ref.id, ...doc });
      }
      if (typeof renderCalendar === 'function') renderCalendar();
    } else if (typeof calLoadPersonalEvents === 'function') {
      const arr = calLoadPersonalEvents();
      arr.push({ id: 'ev' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        type: 'followup', title, date, time: '', notes, topic: '', color: '',
        done: false, createdAt: new Date().toISOString() });
      calSavePersonalEvents(arr);
      if (typeof renderCalendar === 'function') renderCalendar();
    } else {
      throw new Error('The calendar is not available here.');
    }
  }

  /* ======================================================================
     HANDS-FREE CLIENT INTERVIEW
     Asks for one field at a time, out loud, and listens for the answer. The
     point is that a CHW standing in front of somebody can enrol them without
     looking at a screen or typing — so the loop runs here, the microphone
     stays on between questions, and an answer submits itself after a pause.

     The loop is deterministic on purpose. The model parses each reply; it does
     not decide what to ask next. That way every field gets covered, a bad
     parse costs one repeated question instead of derailing the conversation,
     and the CHW can say "skip" to any of it.
     ====================================================================== */

  const AI_CLIENT_FIELDS = [
    { key: 'name',       q: "What's their full name?",                              required: true },
    { key: 'phone',      q: "What's their phone number? Say skip if you don't have it." },
    { key: 'address',    q: "What's their address?" },
    { key: 'birthday',   q: "What's their date of birth?" },
    { key: 'cin',        q: "What are the last four of their CIN?" },
    { key: 'referredBy', q: "Who referred them?" }
  ];

  let aiMode = 'command';   // 'command' | 'clientIv'
  let aiIv = null;          // { values, skipped:Set, idx }

  const aiRefSources = () => {
    try { return (typeof refSrcList === 'function') ? refSrcList() : []; }
    catch (e) { return []; }
  };

  /* The CHW's own first name, for the greeting. Using a name makes a spoken
     interface feel like it is talking TO someone rather than AT them, and it
     costs nothing — this never goes to the API. */
  function aiFirstName() {
    let n = '';
    try { if (typeof myTeamDisplayName === 'function') n = myTeamDisplayName() || ''; } catch (e) {}
    if (!n && typeof currentUser !== 'undefined' && currentUser) {
      n = currentUser.displayName || (currentUser.email || '').split('@')[0] || '';
    }
    n = String(n).trim().split(/[\s.]+/)[0] || '';
    if (!n) return '';
    // "eddypongo2003" from an email stub is not a name anybody wants read aloud.
    if (/\d/.test(n) || n.length > 18) return '';
    return n.charAt(0).toUpperCase() + n.slice(1);
  }

  function aiStartClientInterview(seed) {
    aiMode = 'clientIv';
    aiIv = { values: Object.assign({}, seed || {}), skipped: new Set(), idx: 0, addressDone: false };
    aiTurns = [];
    const who = aiFirstName();
    const hello = (who ? 'Hi ' + who + '. ' : '') + "Let's add a new client.";
    aiTurns.push({ role: 'assistant', text: hello });
    /* Deliberately NOT spoken separately — aiAdvanceInterview speaks the first
       question a few milliseconds later, and the second utterance would cancel
       this one mid-word. The greeting is prepended to that question instead. */
    aiIv.pendingHello = hello;
    aiAdvanceInterview();
  }

  function aiNextField() {
    return AI_CLIENT_FIELDS.find(f =>
      !aiIv.skipped.has(f.key) && !String(aiIv.values[f.key] || '').trim());
  }

  /* Ask the next unanswered question, or stop and show the form when there is
     nothing left to ask. */
  function aiAdvanceInterview() {
    const field = aiNextField();
    if (!field) { aiFinishInterview(); return; }
    aiIv.asking = field.key;
    let q = field.q;
    if (field.key === 'referredBy') {
      const src = aiRefSources();
      if (src.length) q += ' Options are: ' + src.join(', ') + '.';
    }
    aiTurns.push({ role: 'assistant', text: q });
    aiOpenSay(false);
    const spoken = (aiIv.pendingHello ? aiIv.pendingHello + ' ' : '') + q;
    aiIv.pendingHello = null;
    aiSpeak(spoken, true);
    aiArmAutoSend();
  }

  async function aiSendInterviewAnswer(said) {
    // While address choices are on screen, a reply is probably picking one.
    if (aiAddrChoices.length) {
      const pick = aiParseChoice(said);
      if (pick !== null) {
        aiTurns.push({ role: 'user', text: said });
        aiPickAddress(pick);
        return;
      }
      // Not a choice — they are re-stating the address. Start that field over.
      aiHideChoices();
      aiIv.values.address = '';
      aiIv.addressDone = false;
      aiIv.asking = 'address';
    }
    aiTurns.push({ role: 'user', text: said });
    stage('busy');
    $('ai-busy-msg').textContent = 'Got it…';
    try {
      const r = await window.otterAI('extractClient', {
        asking: aiIv.asking,
        known: aiIv.values,
        refSources: aiRefSources(),
        reply: said
      }) || {};

      const v = r.values || {};
      Object.keys(v).forEach(k => {
        if (v[k] != null && String(v[k]).trim()) aiIv.values[k] = String(v[k]).trim();
      });
      if (aiIv.values.cin) {
        // Mirror the manual form exactly: last four, alphanumeric, uppercase.
        aiIv.values.cin = aiIv.values.cin.replace(/[^A-Za-z0-9]/g, '').slice(-4).toUpperCase();
      }

      const field = AI_CLIENT_FIELDS.find(f => f.key === aiIv.asking);
      const stillEmpty = !String(aiIv.values[aiIv.asking] || '').trim();
      if (r.skip && stillEmpty) {
        if (field && field.required) {
          // A client with no name is not a record, it is a mystery.
          aiTurns.push({ role: 'assistant', text: "I do need a name for them. What should I put?" });
          aiOpenSay(false);
          aiSpeak("I do need a name for them. What should I put?", true);
          aiArmAutoSend();
          return;
        }
        aiIv.skipped.add(aiIv.asking);
      } else if (aiIv.asking === 'address' && !stillEmpty && !aiIv.addressDone) {
        // Spoken addresses are the field most likely to come out wrong, so
        // this one gets checked rather than accepted.
        aiOfferAddressMatches(aiIv.values.address);
        return;
      } else if (stillEmpty && !r.skip) {
        // Heard nothing usable. Ask once more rather than silently moving on.
        const again = "Sorry — I didn't catch that. " + (field ? field.q : '');
        aiTurns.push({ role: 'assistant', text: again });
        aiOpenSay(false);
        aiSpeak(again, true);
        aiArmAutoSend();
        return;
      }
      aiAdvanceInterview();
    } catch (e) {
      console.warn('client interview step failed:', e && e.message);
      alert(e && e.message ? e.message : "Couldn't understand that.");
      aiOpenSay(false);
    }
  }

  /* ---- Address confirmation -------------------------------------------
     Addresses are where dictation falls apart. "Twelve fifteen West Olive"
     comes back as "1215 west olives", the geocoder shrugs, and the client
     lands on the mileage map in the wrong city — or nowhere.

     So the spoken address is looked up and the matches offered back. The CHW
     picks one by voice ("the second one"), by tapping, or rejects them all and
     keeps exactly what they said. Keeping their words is a real option: a rural
     address or a shelter that Nominatim has never heard of is still the right
     thing to store, and a wrong "corrected" address is worse than an
     un-geocoded one. */
  let aiAddrChoices = [];

  function aiHideChoices() {
    aiAddrChoices = [];
    const box = $('ai-choices');
    if (box) { box.hidden = true; box.innerHTML = ''; }
  }

  async function aiOfferAddressMatches(spoken) {
    stage('busy');
    $('ai-busy-msg').textContent = 'Checking that address…';
    let results = [];
    try {
      const url = 'https://nominatim.openstreetmap.org/search?q=' +
        encodeURIComponent(spoken) + '&format=json&limit=5&addressdetails=1';
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (res.ok) results = await res.json();
    } catch (e) {
      console.warn('address lookup failed:', e && e.message);
    }

    aiIv.addressDone = true;

    if (!results.length) {
      const msg = 'I couldn\'t find that address, so I\'ll keep it exactly as you said it. You can fix it on their profile later.';
      aiTurns.push({ role: 'assistant', text: msg });
      aiSpeak(msg, true);
      aiAdvanceInterview();
      return;
    }

    aiAddrChoices = results.map(r => r.display_name);
    const msg = results.length === 1
      ? 'I found one match. Say yes to use it, or no to keep what you said.'
      : 'I found ' + results.length + ' matches. Say the number you want, or say none.';
    aiTurns.push({ role: 'assistant', text: msg });
    aiOpenSay(false);

    const box = $('ai-choices');
    box.innerHTML = results.map((r, i) => {
      const parts = String(r.display_name).split(',');
      return `<button type="button" class="ai-choice" data-i="${i}">` +
        `<b>${i + 1}</b>${aiEsc(parts.slice(0, 2).join(','))}` +
        `<small>${aiEsc(parts.slice(2).join(',').trim())}</small></button>`;
    }).join('') +
      `<button type="button" class="ai-choice" data-i="-1"><b>NONE</b>` +
      `Keep what I said: ${aiEsc(spoken)}</button>`;
    box.hidden = false;
    box.querySelectorAll('.ai-choice').forEach(b => {
      b.addEventListener('click', () => aiPickAddress(parseInt(b.dataset.i, 10)));
    });

    aiSpeak(msg, true);
  }

  function aiPickAddress(i) {
    if (i >= 0 && aiAddrChoices[i]) aiIv.values.address = aiAddrChoices[i];
    // i < 0 means "none of these" — whatever they said already stands.
    aiHideChoices();
    aiAdvanceInterview();
  }

  /* Choosing from a spoken list is parsed HERE, not by the model. "Number two"
     is not a language problem worth a network round-trip, and doing it locally
     means it still works when the connection does not. */
  function aiParseChoice(said) {
    const t = String(said || '').toLowerCase().trim();
    if (/\b(none|neither|no thanks|keep (what|mine)|none of (them|those|these)|nope)\b/.test(t)) return -1;
    if (aiAddrChoices.length === 1 && /\b(yes|yeah|yep|correct|that one|use it)\b/.test(t)) return 0;
    const words = { one: 1, two: 2, three: 3, four: 4, five: 5,
                    first: 1, second: 2, third: 3, fourth: 4, fifth: 5 };
    for (const w in words) {
      if (new RegExp('\\b' + w + '\\b').test(t)) return words[w] - 1;
    }
    const d = t.match(/\b([1-5])\b/);
    if (d) return parseInt(d[1], 10) - 1;
    return null;   // not a choice — treat it as a fresh answer
  }

  /* Hand the collected values to the ordinary confirmation screen, so the last
     word is still a form the CHW can read and correct. Hands-free gets you to
     the end; it does not get to skip the check. */
  function aiFinishInterview() {
    aiStopMic();
    aiMode = 'command';
    const v = aiIv.values;
    const summary = 'Ready to add ' + (v.name || 'this client') + '. Check it over before saving.';
    aiSpeak(summary, true);
    aiShowAction({ kind: 'action', action: 'addClient', say: summary, client: v });
  }

  /* Auto-submit after a pause, but only during the interview. In the general
     command box this would be alarming — you would be halfway through a
     sentence and watch it send itself. Here it is the entire point. */
  const AI_AUTOSEND_MS = 1800;
  let aiAutoTimer = null;
  function aiClearAutoSend() { if (aiAutoTimer) { clearTimeout(aiAutoTimer); aiAutoTimer = null; } }
  function aiArmAutoSend() {
    aiClearAutoSend();
    // Only meaningful while the microphone is running.
    if (!aiRecOn) return;
  }
  function aiBumpAutoSend() {
    if (aiMode !== 'clientIv' || !aiRecOn) return;
    aiClearAutoSend();
    aiAutoTimer = setTimeout(() => {
      const said = ($('ai-utterance').value || '').trim();
      if (said) aiSendInterviewAnswer(said);
    }, AI_AUTOSEND_MS);
  }

  /* ---- Speaking back ---------------------------------------------------
     Uses the browser's own speech synthesis, which is free, needs no API, and
     never sends the text anywhere — the voice is generated on the device.

     Only clarifying QUESTIONS are spoken, never record contents. Somebody
     driving between visits wants to hear "how long were you with her?"; they
     do not want their phone reading a client's contact history aloud in a car
     with someone else in it.

     The preference persists, because a CHW who wants this wants it every time
     and a CHW who doesn't will turn it off once and be furious if it returns. */
  /* Defaults to ON. The previous default was off, which meant the assistant
     appeared mute until you found a checkbox you had no reason to look for —
     it read as broken rather than as a setting. Turning it off is one click
     and the choice sticks. */
  const AI_SPEAK_KEY = 'mt.aiSpeakBack';
  const aiSpeakOn = () => {
    try { return localStorage.getItem(AI_SPEAK_KEY) !== '0'; } catch (e) { return true; }
  };

  /* `force` is used by the client interview, which is a spoken conversation by
     definition — silencing its questions would leave the CHW staring at a
     screen they are explicitly trying not to look at. The toggle governs the
     ordinary command box, where speech is a preference rather than the point. */
  /* Pick the least robotic voice available.

     getVoices() is famously empty on first call — the list arrives
     asynchronously and fires 'voiceschanged'. Calling it once at startup and
     caching the answer is how you end up with the default robot forever, so
     this re-reads until it gets something.

     Preference order is deliberate: a named local voice (Samantha, Ava and
     friends on Apple devices, Google's UK/US voices on Chrome) sounds markedly
     more human than whatever the platform picks by default. Local voices are
     also preferred because they work with no connection and add no latency. */
  const AI_VOICE_PICKS = ['samantha', 'ava', 'allison', 'google us english',
                          'google uk english female', 'microsoft aria', 'karen', 'moira'];
  let aiVoice = null, aiVoiceTried = false;

  function aiBestVoice() {
    const synth = window.speechSynthesis;
    if (!synth || !synth.getVoices) return null;
    if (aiVoice) return aiVoice;
    const voices = synth.getVoices();
    if (!voices || !voices.length) return null;
    const lang = (navigator.language || 'en-US').slice(0, 2).toLowerCase();
    const sameLang = voices.filter(v => (v.lang || '').slice(0, 2).toLowerCase() === lang);
    const pool = sameLang.length ? sameLang : voices;
    for (const want of AI_VOICE_PICKS) {
      const hit = pool.find(v => (v.name || '').toLowerCase().includes(want));
      if (hit) { aiVoice = hit; return hit; }
    }
    aiVoice = pool.find(v => v.localService) || pool[0] || null;
    return aiVoice;
  }

  // The list may not exist yet when the page loads; this fills it in when it does.
  if (window.speechSynthesis && !aiVoiceTried) {
    aiVoiceTried = true;
    try {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.addEventListener('voiceschanged', () => { aiVoice = null; aiBestVoice(); });
    } catch (e) {}
  }

  /* Speech synthesis is fiddly in ways that all look like "it just doesn't
     work", so each guard below is here for a specific reason:

     - cancel() immediately followed by speak() is a known Chrome race: the new
       utterance gets swallowed by the cancellation. Cancelling only when
       something is actually speaking avoids it.
     - Chrome drops the engine into a paused state after periods of inactivity
       and never tells you. resume() is a no-op when it isn't paused.
     - The voice list can still be empty on the first call even after
       voiceschanged, so a missing voice must not stop us speaking at all.
     - onerror was silent before, which is exactly why this looked like nothing
       happening rather than something failing. */
  function aiSpeak(text, force) {
    if (!text) return;
    if (!force && !aiSpeakOn()) return;
    const synth = window.speechSynthesis;
    if (!synth) { console.warn('[ai] speech synthesis not available in this browser'); return; }
    try {
      if (synth.speaking || synth.pending) synth.cancel();
      synth.resume();
      const u = new SpeechSynthesisUtterance(String(text));
      const v = aiBestVoice();
      if (v) { u.voice = v; u.lang = v.lang || navigator.language || 'en-US'; }
      else { u.lang = navigator.language || 'en-US'; }
      u.rate = 1.0;
      u.pitch = 1.0;
      u.onerror = ev => console.warn('[ai] speech error:', ev && ev.error);
      // Small defer: speaking in the same tick as a DOM update is another way
      // Chrome silently drops the utterance.
      setTimeout(() => { try { synth.speak(u); } catch (e) { console.warn('[ai] speak failed:', e); } }, 60);
    } catch (e) {
      console.warn('[ai] speech synthesis failed:', e);
    }
  }
  function aiStopSpeaking() {
    try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) {}
  }

  /* ---- Dictation -------------------------------------------------------
     Same Web Speech API as Meeting Mode, but a separate recognizer: sharing
     one would mean this panel could stop a dictation that a note is still
     using. On Chrome and Edge the audio goes to the browser vendor's speech
     service, which is why the panel says so rather than implying it is local. */
  const AI_SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  let aiRec = null, aiRecOn = false;

  /* Read by idleBusy(). Dictation running is not an idle browser, and this has
     to be a live check for the same reason as mmBusy above. */
  window.aiBusy = () => !!aiRecOn;

  function aiStopMic() {
    if (aiRec && aiRecOn) { try { aiRec.stop(); } catch (e) {} }
    aiRecOn = false;
    const b = $('ai-mic'); if (b) b.classList.remove('on');
    const n = $('ai-mic-note'); if (n) n.hidden = true;
  }

  function aiToggleMic() {
    if (!AI_SR) {
      alert('Dictation needs Chrome, Edge or Safari. You can type instead.');
      return;
    }
    if (aiRecOn) { aiStopMic(); return; }
    aiRec = new AI_SR();
    aiRec.continuous = true;
    aiRec.interimResults = false;
    aiRec.lang = navigator.language || 'en-US';
    aiRec.onresult = ev => {
      let add = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) add += ev.results[i][0].transcript;
      }
      if (add) {
        const box = $('ai-utterance');
        box.value = (box.value ? box.value.trim() + ' ' : '') + add.trim();
        aiBumpAutoSend();   // no-op outside the hands-free interview
      }
    };
    aiRec.onerror = e => {
      console.warn('AI dictation error:', e && e.error);
      aiStopMic();
      if (e && e.error === 'not-allowed') alert('Microphone access was blocked. You can type instead.');
    };
    aiRec.onend = () => { if (aiRecOn) { try { aiRec.start(); } catch (e) { aiStopMic(); } } };
    try {
      aiRec.start();
      aiRecOn = true;
      $('ai-mic').classList.add('on');
      $('ai-mic-note').hidden = false;
    } catch (e) {
      console.warn('Could not start dictation:', e);
      aiStopMic();
    }
  }

  // --- wiring ---
  /* The only way into the reader now. Opens the panel, then the file picker —
     the panel is still where the camera, the downscaling, the EXIF stripping
     and the confirm screen live. */
  window.otterScanDocument = function otterScanDocument() {
    aiOpen();
    setTimeout(() => { const f = $('ai-file'); if (f) f.click(); }, 80);
  };

  const navBtn = $('nav-ai-btn');
  if (navBtn) navBtn.addEventListener('click', aiOpen);
  $('ai-close').addEventListener('click', aiClose);
  overlay.addEventListener('click', e => { if (e.target === overlay) aiClose(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && !overlay.hidden) aiClose(); });
  $('ai-file').addEventListener('change', e => aiHandleFile(e.target.files && e.target.files[0]));
  $('ai-say-something').addEventListener('click', () => aiOpenSay(true));
  $('ai-say-back').addEventListener('click', () => { aiStopMic(); stage('pick'); });
  $('ai-say-send').addEventListener('click', aiSendUtterance);
  $('ai-mic').addEventListener('click', aiToggleMic);
  const speakBox = $('ai-speak-toggle');
  if (speakBox) {
    if (!window.speechSynthesis) {
      // No point offering a switch that does nothing.
      speakBox.closest('.ai-speak-row').hidden = true;
    } else {
      speakBox.checked = aiSpeakOn();
      speakBox.addEventListener('change', () => {
        try { localStorage.setItem(AI_SPEAK_KEY, speakBox.checked ? '1' : '0'); } catch (e) {}
        if (!speakBox.checked) aiStopSpeaking();
      });
    }
  }
  // Enter sends; Shift+Enter is a newline, as everywhere else.
  $('ai-utterance').addEventListener('keydown', e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); aiSendUtterance(); }
  });
  $('ai-answer-again').addEventListener('click', () => aiOpenSay(true));
  $('ai-answer-done').addEventListener('click', aiClose);
  $('ai-act-cancel').addEventListener('click', () => aiOpenSay(false));
  $('ai-act-save').addEventListener('click', aiSaveAction);
  $('ai-discard').addEventListener('click', aiClose);
  $('ai-save').addEventListener('click', aiSave);
  document.querySelectorAll('.ai-dest-btn').forEach(b => {
    b.addEventListener('click', () => aiSetDest(b.dataset.dest));
  });
}

onReady(initAiPanel);
